import React from "react";
import {
  User,
  Mail,
  Briefcase,
  Clock,
  Phone,
  Languages,
  ChevronDown,
} from "lucide-react";
import ImageUpload from "./ImageUpload";

const timezones = [
  "(GMT+05:30) India Standard Time",
  "(GMT+00:00) Coordinated Universal Time",
  "(GMT-05:00) Eastern Time",
  "(GMT-08:00) Pacific Time",
  "(GMT+01:00) Central European Time",
];

const languages = ["English (US)", "English (UK)", "Hindi", "Spanish", "German", "French"];

// Decorative only: an ID card behind a soft blob, echoing "this is how you
// appear to your teammates" without shipping a raster asset.
const IdCardArt = () => (
  <svg className="acct-hero-art" viewBox="0 0 220 150" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="acct-blob" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#e8e9fb" />
        <stop offset="100%" stopColor="#f1f1fd" />
      </linearGradient>
    </defs>

    <path
      d="M56 36c22-26 64-34 96-22 33 12 55 44 52 74-3 29-30 56-64 58-34 2-75-20-86-48-11-28-20-36 2-62z"
      fill="url(#acct-blob)"
    />

    <rect x="66" y="48" width="112" height="70" rx="12" fill="#ffffff" />
    <circle cx="98" cy="76" r="13" fill="#c7cbfa" />
    <path d="M87 100c2-8 6-12 11-12s9 4 11 12z" fill="#c7cbfa" />
    <rect x="122" y="66" width="44" height="7" rx="3.5" fill="#c7cbfa" />
    <rect x="122" y="81" width="34" height="6" rx="3" fill="#dfe1fc" />
    <rect x="122" y="94" width="40" height="6" rx="3" fill="#dfe1fc" />

    <path
      d="M186 24l-6 16M198 32l-12 11M202 48l-15 4"
      stroke="#4f52ea"
      strokeWidth="5"
      strokeLinecap="round"
    />
  </svg>
);

// A field whose icon sits inside the control, so the row reads as one object
// rather than a label, an icon and a box.
const Field = ({ label, icon: Icon, children }) => (
  <label className="acct-field">
    <span className="cz-field-label">{label}</span>
    <span className="acct-input-wrap">
      <Icon size={17} strokeWidth={1.9} className="acct-input-icon" />
      {children}
    </span>
  </label>
);

export default function ProfileCard({ profile, onChange }) {
  // The name is blank until the account loads, and can be null on the record,
  // so this can't index straight into split() results.
  const initials =
    (profile.fullName || profile.email || "")
      .trim()
      .split(/\s+/)
      .map((part) => part.charAt(0))
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?";

  return (
    <section className="dash-card acct-profile-card">
      <div className="acct-hero">
        <ImageUpload
          layout="hero"
          value={profile.avatar}
          onChange={(value) => onChange("avatar", value)}
          fallback={initials}
          alt="Profile"
          hint="JPG or PNG, at least 200×200px and under 2 MB."
        >
          <h2 className="acct-hero-title">Personal information</h2>
          <p className="acct-hero-sub">
            This is how you appear to your teammates inside the workspace.
          </p>
        </ImageUpload>

        <IdCardArt />
      </div>

      <div className="acct-field-grid">
        <Field label="Full name" icon={User}>
          <input
            type="text"
            className="review-input"
            placeholder="Your name"
            value={profile.fullName}
            onChange={(e) => onChange("fullName", e.target.value)}
          />
        </Field>

        <Field label="Email address" icon={Mail}>
          {/* The account identity, and there is no endpoint to change it. */}
          <input
            type="email"
            className="review-input"
            value={profile.email}
            readOnly
            aria-readonly="true"
          />
        </Field>

        <Field label="Job title" icon={Briefcase}>
          <input
            type="text"
            className="review-input"
            placeholder="Enter your job title"
            value={profile.jobTitle}
            onChange={(e) => onChange("jobTitle", e.target.value)}
          />
        </Field>

        <Field label="Timezone" icon={Clock}>
          <select
            className="review-input acct-select"
            value={profile.timezone}
            onChange={(e) => onChange("timezone", e.target.value)}
          >
            {timezones.map((zone) => (
              <option key={zone}>{zone}</option>
            ))}
          </select>
          <ChevronDown size={17} strokeWidth={1.9} className="acct-select-chevron" />
        </Field>

        <Field label="Phone number" icon={Phone}>
          <input
            type="tel"
            className="review-input"
            placeholder="Add a phone number"
            value={profile.phone}
            onChange={(e) => onChange("phone", e.target.value)}
          />
        </Field>

        <Field label="Language" icon={Languages}>
          <select
            className="review-input acct-select"
            value={profile.language}
            onChange={(e) => onChange("language", e.target.value)}
          >
            {languages.map((lang) => (
              <option key={lang}>{lang}</option>
            ))}
          </select>
          <ChevronDown size={17} strokeWidth={1.9} className="acct-select-chevron" />
        </Field>
      </div>
    </section>
  );
}
